import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Modal, Checkbox, DatePicker, TimePicker } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import Dropdown from 'components/Elements/Dropdown';
import { DropdownOption } from 'components/Elements/Dropdown/types';
import { CalendarDarkIcon, ClockDarkIcon } from 'components/Elements/CustomSVGIcon';
import {
    Incident,
    IncidentEmailPreview,
    MitieDutyManagerInformed,
    MITIE_DUTY_MANAGER_INFORMED_OPTIONS,
} from 'core/incidents/types';
import { isRetailSiteId } from '../../retailSite';
import EmailPreviewModal from '../EmailPreviewModal';
import './editIncidentModal.scss';

const MAX_DESCRIPTION_LENGTH = 2000;
const MAX_REFERENCE_LENGTH = 100;
const MAX_REASON_LENGTH = 500;

// New incidents always start in this status. The Status field is hidden on
// create, so it is fixed to this value instead of being chosen by the user.
const DEFAULT_INCIDENT_STATUS_LABEL = 'Awaiting Attendance';

// Options for the Problem Category filter dropdown. Purely a search aid —
// it narrows the list shown, it does not change the incident.
const CATEGORY_FILTER_OPTIONS = [
    { key: 'all', label: 'All categories' },
    { key: 'critical', label: 'Critical only' },
];

export interface CategoryFieldConfig {
    showRemedyReference: boolean;
    showKhcReference: boolean;
    showMaximoReference: boolean;
    showConceptReference: boolean;
    showPoliceReference: boolean;
    requireCheckInOut: boolean;
    defaultPriorityId: number | null;
    visibleRetail: boolean;
    isCriticalCategory: boolean;
    // Empty array = all active priorities allowed.
    priorityIds: number[];
}

// The conditional-field flags for the selected category.
export interface EffectiveFieldConfig {
    showRemedyReference: boolean;
    showKhcReference: boolean;
    showMaximoReference: boolean;
    showConceptReference: boolean;
    showPoliceReference: boolean;
    requireCheckInOut: boolean;
}

/**
 * Resolve the conditional-field config for the selected category. Every flag,
 * including "police assigned", comes from the category itself — the police
 * question is what separates Single from Confirmed Intruder Alarm.
 */
export const resolveEffectiveConfig = (
    config: CategoryFieldConfig | undefined | null,
): EffectiveFieldConfig => ({
    showRemedyReference: config?.showRemedyReference ?? false,
    showKhcReference: config?.showKhcReference ?? false,
    showMaximoReference: config?.showMaximoReference ?? false,
    showConceptReference: config?.showConceptReference ?? false,
    showPoliceReference: config?.showPoliceReference ?? false,
    requireCheckInOut: config?.requireCheckInOut ?? false,
});

export interface EditIncidentValues {
    site: string;
    problemCategory: string;
    maximoReference: string;
    remedyReference: string;
    khcReference: string;
    conceptReference: string;
    policeReference: string;
    policeAssigned: string;
    reason: string;
    status: string;
    priority: string;
    description: string;
    critical: boolean;
    mitieDutyManagerInformed: MitieDutyManagerInformed | '';
    checkedInAt: string | null;
    checkedOutAt: string | null;
    // Edit only: whether saving also reports the incident to the distribution
    // list. Mirrors the diary entry's "send report to distribution centre" tick.
    sendToDistribution: boolean;
}

export type OnSaveHandler = (values: EditIncidentValues) => Promise<boolean> | void;

export interface EditIncidentOptions {
    sites: DropdownOption[];
    problemCategories: DropdownOption[];
    statuses: DropdownOption[];
    priorities: DropdownOption[];
    categoryFieldConfig: Record<string, CategoryFieldConfig>;
    // Site type name per site id — display value, and the legacy fallback for
    // the retail check.
    siteTypes: Record<string, string>;
    // Guarding type per site id ('retail' / 'non_retail'), which is what the
    // retail rules actually key off. See retailSite.ts.
    siteGuardingTypes?: Record<string, string>;
}

interface EditIncidentModalProps {
    visible: boolean;
    onClose: () => void;
    onSave: OnSaveHandler;
    incident: Incident | null;
    editOptions: EditIncidentOptions;
    isSiteMode?: boolean;
    siteId?: string;
    siteName?: string;
    // Fetches a preview of the distribution email for the pending edit. When
    // provided, a "Preview email" button is shown while "send report to
    // distribution centre" is ticked. Returns null if the preview could not be
    // built. Edit only — creating an incident always notifies, so there is
    // nothing to opt into (or preview) on create.
    // eslint-disable-next-line react/require-default-props
    onPreview?: (values: EditIncidentValues) => Promise<IncidentEmailPreview | null>;
}

interface FieldErrors {
    site?: string;
    problemCategory?: string;
    remedyReference?: string;
    khcReference?: string;
    maximoReference?: string;
    conceptReference?: string;
    policeAssigned?: string;
    policeReference?: string;
    reason?: string;
    priority?: string;
    description?: string;
    status?: string;
    checkIn?: string;
    checkOut?: string;
    mitieDutyManagerInformed?: string;
}

const getSubmitButtonLabel = (submitting: boolean, isEdit: boolean): string => {
    if (submitting) return isEdit ? 'Updating...' : 'Creating...';
    return isEdit ? 'Update Incident' : 'Create Incident';
};

const clearErrors = (prev: FieldErrors, ...keys: (keyof FieldErrors)[]): FieldErrors => {
    const next = { ...prev };
    keys.forEach((k) => { delete next[k]; });
    return next;
};

const EditIncidentModal: React.FC<EditIncidentModalProps> = ({
    visible,
    onClose,
    onSave,
    incident,
    editOptions,
    isSiteMode = false,
    siteId = '',
    siteName = '',
    onPreview,
}) => {
    const [site, setSite] = useState('');
    const [problemCategory, setProblemCategory] = useState('');
    const [maximoReference, setMaximoReference] = useState('');
    const [remedyReference, setRemedyReference] = useState('');
    const [khcReference, setKhcReference] = useState('');
    const [conceptReference, setConceptReference] = useState('');
    const [policeReference, setPoliceReference] = useState('');
    const [policeAssigned, setPoliceAssigned] = useState('');
    const [reason, setReason] = useState('');
    const [status, setStatus] = useState('');
    const [priority, setPriority] = useState('');
    const [description, setDescription] = useState('');
    const [critical, setCritical] = useState(false);
    const [sendToDistribution, setSendToDistribution] = useState(false);
    const [mitieDutyManagerInformed, setMitieDutyManagerInformed] = useState<MitieDutyManagerInformed | ''>('');
    // Purely a search aid for the category dropdown — NOT the Critical flag.
    const [criticalCategoriesOnly, setCriticalCategoriesOnly] = useState(false);
    const [checkInDate, setCheckInDate] = useState<Dayjs | null>(null);
    const [checkInTimePicker, setCheckInTimePicker] = useState<Dayjs | null>(null);
    const [checkOutDate, setCheckOutDate] = useState<Dayjs | null>(null);
    const [checkOutTimePicker, setCheckOutTimePicker] = useState<Dayjs | null>(null);
    const [errors, setErrors] = useState<FieldErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewData, setPreviewData] = useState<IncidentEmailPreview | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    // Synchronous lock: setIsPreviewing(true) is async, so a rapid double-click
    // in the same tick could fire two preview requests before state updates.
    const previewInFlightRef = useRef(false);
    const categoryUserChanged = useRef(false);
    // Holds the Critical value from just before a critical-by-default category
    // forced it on, so switching back to a normal category restores the user's
    // own choice instead of leaving the forced tick behind. Null = not forced.
    const criticalBeforeForce = useRef<boolean | null>(null);
    // Previous effective-critical value, so the reset below only runs on a
    // true -> false transition and never on mount.
    const wasCriticalIncident = useRef(false);

    const defaultFieldConfig: CategoryFieldConfig = {
        showRemedyReference: true,
        showKhcReference: true,
        showMaximoReference: true,
        showConceptReference: true,
        showPoliceReference: true,
        requireCheckInOut: false,
        defaultPriorityId: null,
        visibleRetail: true,
        isCriticalCategory: false,
        priorityIds: [],
    };

    const fieldConfig = useMemo(() => {
        if (!problemCategory) return null;
        return editOptions.categoryFieldConfig[problemCategory] || defaultFieldConfig;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problemCategory, editOptions.categoryFieldConfig]);

    const isRetailSite = useMemo(
        () => isRetailSiteId(editOptions, site),
        [site, editOptions],
    );

    const effectiveConfig = useMemo(
        () => resolveEffectiveConfig(fieldConfig),
        [fieldConfig],
    );

    const showRemedyReference = !!(problemCategory && effectiveConfig.showRemedyReference && !isRetailSite);

    // Categories that are VALID for the current site type. Non-retail sites
    // always allow every category; retail sites are filtered by visibleRetail.
    // This is the set a selection is checked against — the "critical only"
    // filter must never make a legitimately-chosen category look invalid.
    const siteVisibleCategories = useMemo(() => editOptions.problemCategories.filter((opt) => {
        const cfg = editOptions.categoryFieldConfig[opt.value];
        if (!cfg) return true;
        // visibleRetail defaults to visible — only an explicit false hides.
        return !site || !isRetailSite || cfg.visibleRetail !== false;
    }), [editOptions.problemCategories, editOptions.categoryFieldConfig, site, isRetailSite]);

    // Categories SHOWN in the open list. On create, "critical only" narrows it
    // to critical categories — including hiding a non-critical one that is
    // currently selected, so browsing the filtered list isn't confusing. The
    // selection itself is untouched (see the display value below): the user can
    // look around and close the list with their choice still in place.
    const availableCategories = useMemo(() => {
        if (incident || !criticalCategoriesOnly) return siteVisibleCategories;
        return siteVisibleCategories.filter(
            (opt) => !!editOptions.categoryFieldConfig[opt.value]?.isCriticalCategory,
        );
    }, [siteVisibleCategories, editOptions.categoryFieldConfig, incident, criticalCategoriesOnly]);

    // Whether the selected category forces the incident to be Critical.
    const selectedCategoryIsCritical = useMemo(() => {
        const cfg = problemCategory ? editOptions.categoryFieldConfig[problemCategory] : null;
        return !!cfg?.isCriticalCategory;
    }, [problemCategory, editOptions.categoryFieldConfig]);

    // A critical-by-default category always produces a critical incident; on any
    // other category the checkbox alone decides, so any incident can still be
    // marked critical by hand. Retail stores are never critical — the checkbox
    // is hidden for them and the payload must agree (the backend forces the
    // same thing in ValidatesIncidentCategory).
    const isCriticalIncident = !isRetailSite && (critical || selectedCategoryIsCritical);

    // Whether the "Mitie Duty Manager Informed" question applies. Both the field
    // and its required-rule read this one value, so the rule can never fire for
    // a field that isn't on screen.
    const showMitieDutyManager = isCriticalIncident;

    // Priorities allowed for the selected category (empty allowlist = all).
    const availablePriorities = useMemo(() => {
        const cfg = problemCategory ? editOptions.categoryFieldConfig[problemCategory] : null;
        if (!cfg || !cfg.priorityIds?.length) return editOptions.priorities;
        return editOptions.priorities.filter((p) => cfg.priorityIds.includes(Number(p.value)));
    }, [problemCategory, editOptions.priorities, editOptions.categoryFieldConfig]);

    // Value of the default status ("Awaiting Attendance") used for new incidents.
    // Fall back to the first available status if the label ever changes, so the
    // hidden status field is never left empty (which would fail create validation).
    const defaultStatusValue = useMemo(
        () => editOptions.statuses.find((opt) => opt.label === DEFAULT_INCIDENT_STATUS_LABEL)?.value
            ?? editOptions.statuses[0]?.value
            ?? '',
        [editOptions.statuses],
    );

    // Populate fields when editing an existing incident
    useEffect(() => {
        if (visible && incident) {
            setSite(incident.site ? String(incident.site.id) : '');
            setProblemCategory(incident.incidentProblemCategory ? String(incident.incidentProblemCategory.id) : '');
            setMaximoReference(incident.maximoReference || '');
            setRemedyReference(incident.remedyReference || '');
            setKhcReference(incident.khcReference || '');
            setConceptReference(incident.conceptReference || '');
            setPoliceReference(incident.policeReference || '');
            let policeVal = '';
            if (incident.policeAssigned === true) policeVal = 'yes';
            else if (incident.policeAssigned === false) policeVal = 'no';
            setPoliceAssigned(policeVal);
            setReason(incident.reason || '');
            setStatus(incident.incidentStatus ? String(incident.incidentStatus.id) : '');
            setPriority(incident.incidentPriority ? String(incident.incidentPriority.id) : '');
            setDescription(incident.description || '');
            setCritical(incident.isCritical ?? false);
            // Always starts unticked: reporting an edit to the distribution list
            // is a deliberate choice each time, never carried over.
            setSendToDistribution(false);
            setMitieDutyManagerInformed(incident.mitieDutyManagerInformed ?? '');
            if (incident.checkedInAt) {
                const checkIn = dayjs(incident.checkedInAt);
                setCheckInDate(checkIn);
                setCheckInTimePicker(checkIn);
            } else {
                setCheckInDate(null);
                setCheckInTimePicker(null);
            }
            if (incident.checkedOutAt) {
                const checkOut = dayjs(incident.checkedOutAt);
                setCheckOutDate(checkOut);
                setCheckOutTimePicker(checkOut);
            } else {
                setCheckOutDate(null);
                setCheckOutTimePicker(null);
            }
            categoryUserChanged.current = false;
            criticalBeforeForce.current = null;
            setCriticalCategoriesOnly(false);
            setErrors({});
            return;
        }

        if (visible && !incident) {
            setSite(isSiteMode ? siteId : '');
            setProblemCategory('');
            setMaximoReference('');
            setRemedyReference('');
            setKhcReference('');
            setConceptReference('');
            setPoliceReference('');
            setPoliceAssigned('');
            setReason('');
            // Status is hidden on create and fixed to the default.
            setStatus(defaultStatusValue);
            setPriority('');
            setDescription('');
            setCritical(false);
            setSendToDistribution(false);
            setMitieDutyManagerInformed('');
            setCheckInDate(null);
            setCheckInTimePicker(null);
            setCheckOutDate(null);
            setCheckOutTimePicker(null);
            categoryUserChanged.current = false;
            criticalBeforeForce.current = null;
            setCriticalCategoriesOnly(false);
            setErrors({});
        }
    }, [visible, incident, isSiteMode, siteId, defaultStatusValue]);

    // Reset isSubmitting only when the modal opens, never on prop changes mid-request
    useEffect(() => {
        if (visible) {
            setIsSubmitting(false);
            setPreviewVisible(false);
            setPreviewData(null);
            setIsPreviewing(false);
            previewInFlightRef.current = false;
        }
    }, [visible]);

    // Auto-set priority and reset the sub-category when the user changes category.
    useEffect(() => {
        if (!visible || !problemCategory || !categoryUserChanged.current) return;

        const config = editOptions.categoryFieldConfig[problemCategory];
        if (!config) return;

        // Only auto-set priority in create mode (no incident)
        if (!incident) {
            if (config.defaultPriorityId !== null) {
                setPriority(String(config.defaultPriorityId));
            } else {
                setPriority('');
            }
        }

        // Clear errors when category changes
        setErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [problemCategory]);

    // A critical incident category forces the Critical flag on (the checkbox is
    // then locked). Runs on both selection and edit-load so the saved value is
    // always consistent with the category. Switching back to a normal category
    // restores whatever the flag was before the force — a tick the user never
    // made shouldn't survive the category change, but one they did make should.
    useEffect(() => {
        if (selectedCategoryIsCritical) {
            if (criticalBeforeForce.current === null) criticalBeforeForce.current = critical;
            setCritical(true);
        } else if (criticalBeforeForce.current !== null) {
            setCritical(criticalBeforeForce.current);
            criticalBeforeForce.current = null;
            setCriticalCategoriesOnly(false);
        }
    // `critical` is deliberately not a dependency: this must only fire when the
    // category's critical-ness changes, capturing the value from that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategoryIsCritical]);

    // The user ticking/unticking Critical themselves. Their choice becomes the
    // new baseline, so the force effect above must not restore an older value
    // over the top of it. The box is independent of the category filter, so
    // toggling it never disturbs the selected category.
    const handleCriticalChange = (checked: boolean) => {
        criticalBeforeForce.current = null;
        setCritical(checked);
    };

    // Drop the Mitie answer when the incident stops being critical: the field
    // is gone from the screen, so leaving the value in state would resurface a
    // stale answer if Critical is ticked again in the same session.
    //
    // Guarded on the previous value rather than firing on mount, because on the
    // first commit this runs *after* the populate effect above but still sees
    // the pre-populate isCriticalIncident — clearing unconditionally would wipe
    // the answer just loaded for an existing critical incident.
    useEffect(() => {
        if (wasCriticalIncident.current && !isCriticalIncident) {
            setMitieDutyManagerInformed('');
        }
        wasCriticalIncident.current = isCriticalIncident;
    }, [isCriticalIncident]);

    // Clear the selected category if a SITE-TYPE change makes it invalid (also
    // drops its now-orphaned sub-category). Create mode only — an existing
    // incident keeps its saved category when editing.
    //
    // Deliberately checks siteVisibleCategories, not the filtered display list:
    // hiding a category behind the "critical only" filter must never clear a
    // selection the user already made.
    useEffect(() => {
        if (incident || !problemCategory) return;
        if (!siteVisibleCategories.some((o) => o.value === problemCategory)) {
            setProblemCategory('');
        }
    }, [siteVisibleCategories, problemCategory, incident]);

    // Clear the priority if the category's allowlist no longer permits it. Create
    // mode only — an existing incident keeps its saved priority when editing.
    useEffect(() => {
        if (incident) return;
        if (priority && !availablePriorities.some((p) => p.value === priority)) {
            setPriority('');
        }
    }, [availablePriorities, priority, incident]);

    const validate = useCallback((): FieldErrors => {
        const errs: FieldErrors = {};
        if (!site) errs.site = 'Site is required.';
        if (!problemCategory) errs.problemCategory = 'Problem Category is required.';
        if (showRemedyReference) {
            if (!remedyReference.trim()) errs.remedyReference = 'Remedy Reference is required.';
            else if (remedyReference.length > MAX_REFERENCE_LENGTH) errs.remedyReference = `Remedy Reference must not exceed ${MAX_REFERENCE_LENGTH} characters.`;
        }
        if (effectiveConfig.showKhcReference) {
            if (!khcReference.trim()) errs.khcReference = 'KHC Reference is required.';
            else if (khcReference.length > MAX_REFERENCE_LENGTH) errs.khcReference = `KHC Reference must not exceed ${MAX_REFERENCE_LENGTH} characters.`;
        }
        if (effectiveConfig.showMaximoReference) {
            if (!maximoReference.trim()) errs.maximoReference = 'Maximo Reference is required.';
            else if (maximoReference.length > MAX_REFERENCE_LENGTH) errs.maximoReference = `Maximo Reference must not exceed ${MAX_REFERENCE_LENGTH} characters.`;
        }
        if (effectiveConfig.showConceptReference) {
            if (!conceptReference.trim()) errs.conceptReference = 'Concept Reference is required.';
            else if (conceptReference.length > MAX_REFERENCE_LENGTH) errs.conceptReference = `Concept Reference must not exceed ${MAX_REFERENCE_LENGTH} characters.`;
        }
        if (effectiveConfig.showPoliceReference) {
            if (!policeAssigned) errs.policeAssigned = 'Police Assigned is required.';
            if (policeAssigned === 'yes') {
                if (!policeReference.trim()) errs.policeReference = 'Police Reference is required.';
                else if (policeReference.length > MAX_REFERENCE_LENGTH) errs.policeReference = `Police Reference must not exceed ${MAX_REFERENCE_LENGTH} characters.`;
            }
            if (policeAssigned === 'no') {
                if (!reason.trim()) errs.reason = 'Reason is required when police are not assigned.';
                else if (reason.length > MAX_REASON_LENGTH) errs.reason = `Reason must not exceed ${MAX_REASON_LENGTH} characters.`;
            }
        }
        if (showMitieDutyManager && !mitieDutyManagerInformed) {
            errs.mitieDutyManagerInformed = 'Mitie Duty Manager Informed is required for critical incidents.';
        }
        // Retail incidents do not use a priority — the field is hidden for them.
        if (!isRetailSite && !priority) errs.priority = 'Priority is required.';
        if (!status) errs.status = 'Status is required.';
        if (!description.trim()) errs.description = 'Incident Description is required.';
        else if (description.length > MAX_DESCRIPTION_LENGTH) errs.description = `Description must not exceed ${MAX_DESCRIPTION_LENGTH} characters.`;
        if (incident) {
            // Check-in/out are mandatory only when the effective category config
            // requires them; otherwise they stay optional on edit.
            if (effectiveConfig.requireCheckInOut) {
                if (!checkInDate || !checkInTimePicker) errs.checkIn = 'Check-in date and time are required.';
                if (!checkOutDate || !checkOutTimePicker) errs.checkOut = 'Check-out date and time are required.';
            }
            if (checkInDate && checkInTimePicker && checkOutDate && checkOutTimePicker) {
                const checkIn = checkInDate.hour(checkInTimePicker.hour()).minute(checkInTimePicker.minute()).second(0);
                const checkOut = checkOutDate.hour(checkOutTimePicker.hour()).minute(checkOutTimePicker.minute()).second(0);
                if (checkOut.isBefore(checkIn)) {
                    errs.checkOut = 'Check-out must be on or after check-in.';
                }
            }
        }
        return errs;
    }, [
        site, problemCategory, effectiveConfig, showRemedyReference,
        remedyReference, khcReference, maximoReference, conceptReference, policeAssigned, policeReference,
        reason, priority, description, status, incident, checkInDate,
        isCriticalIncident, showMitieDutyManager, mitieDutyManagerInformed, isRetailSite,
        checkInTimePicker, checkOutDate, checkOutTimePicker,
    ]);

    // Validate the form and build the submit values, or return null (setting the
    // relevant errors) when invalid. Shared by save and preview so the two can't
    // validate differently, and so the preview is built from exactly the values
    // a save would send.
    const buildValidatedValues = (): EditIncidentValues | null => {
        const fieldErrors = validate();
        if (Object.keys(fieldErrors).length > 0) {
            setErrors(fieldErrors);
            return null;
        }
        setErrors({});

        let checkedInAt: string | null = null;
        if (checkInDate && checkInTimePicker) {
            checkedInAt = checkInDate
                .hour(checkInTimePicker.hour())
                .minute(checkInTimePicker.minute())
                .second(0)
                .toISOString();
        }

        let checkedOutAt: string | null = null;
        if (checkOutDate && checkOutTimePicker) {
            checkedOutAt = checkOutDate
                .hour(checkOutTimePicker.hour())
                .minute(checkOutTimePicker.minute())
                .second(0)
                .toISOString();
        }

        return {
            site,
            problemCategory,
            maximoReference,
            remedyReference,
            khcReference,
            conceptReference,
            policeReference,
            policeAssigned,
            reason,
            status,
            priority,
            description,
            // Effective value, not the raw checkbox: a critical-by-default
            // category forces this on, and the payload must never disagree
            // with the locked UI (which would also drop the Mitie answer).
            critical: isCriticalIncident,
            // Only asked on critical incidents, so clear it here rather than
            // relying on every caller to strip a value the user left behind
            // after un-ticking Critical.
            mitieDutyManagerInformed: showMitieDutyManager ? mitieDutyManagerInformed : '',
            checkedInAt,
            checkedOutAt,
            // Create always notifies the distribution list, so the opt-in
            // only ever applies to an edit.
            sendToDistribution: !!incident && sendToDistribution,
        };
    };

    const handleSave = async () => {
        if (isSubmitting) return;
        const values = buildValidatedValues();
        if (!values) return;

        setIsSubmitting(true);
        try {
            const success = await Promise.resolve(onSave(values));
            if (success !== false) onClose();
        } catch {
            // Save failed — modal stays open so the user can retry
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePreview = async () => {
        // Ref guard is synchronous, so it blocks a second click in the same tick
        // that the isPreviewing state update hasn't reflected yet.
        if (previewInFlightRef.current || !onPreview) return;
        const values = buildValidatedValues();
        if (!values) return;

        previewInFlightRef.current = true;
        setIsPreviewing(true);
        setPreviewData(null);
        setPreviewVisible(true);
        try {
            const result = await onPreview(values);
            if (result) {
                setPreviewData(result);
            } else {
                // Preview failed (error already surfaced by the thunk) — close it.
                setPreviewVisible(false);
            }
        } catch {
            setPreviewVisible(false);
        } finally {
            previewInFlightRef.current = false;
            setIsPreviewing(false);
        }
    };

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const policeAssignedOptions: DropdownOption[] = useMemo(() => [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' },
    ], []);

    // Pinned to the top of the Problem Category list: narrows the options to
    // critical categories. Purely a search aid — completely separate from the
    // Critical Incident flag further down the form.
    // Hidden on retail: no critical category is visible on a retail site, so
    // the "Critical only" option would only ever empty the list.
    const categoryFilterHeader = (incident || isRetailSite) ? null : (
        <div className="category-filter">
            <FilterOutlined className="category-filter-icon" aria-hidden="true" />
            {CATEGORY_FILTER_OPTIONS.map((option) => (
                <button
                    key={option.key}
                    type="button"
                    className={`category-filter-option${
                        (option.key === 'critical') === criticalCategoriesOnly ? ' is-active' : ''
                    }`}
                    // The list lives inside the dropdown, so stop the click from
                    // bubbling out and closing it.
                    onClick={(e) => {
                        e.stopPropagation();
                        setCriticalCategoriesOnly(option.key === 'critical');
                    }}>
                    {option.label}
                </button>
            ))}
        </div>
    );

    const mitieDutyManagerOptions: DropdownOption[] = useMemo(
        () => MITIE_DUTY_MANAGER_INFORMED_OPTIONS.map((opt) => ({ label: opt.label, value: opt.value })),
        [],
    );

    const getDropdownValue = (value: string, options: DropdownOption[]): DropdownOption[] => {
        const found = options.find(opt => opt.value === value);
        return found ? [found] : [];
    };

    const getSiteDropdownValue = (): DropdownOption[] => {
        if (isSiteMode && !incident && siteId) {
            const found = editOptions.sites.find(opt => opt.value === siteId);
            if (found) return [found];
            return [{ label: siteName, value: siteId }];
        }
        return getDropdownValue(site, editOptions.sites);
    };

    const renderError = (field: keyof FieldErrors) => (
        errors[field] ? <span className="field-error">{errors[field]}</span> : null
    );

    return (
        <Modal
            open={visible}
            onCancel={handleClose}
            footer={null}
            width={540}
            className="edit-incident-modal"
            centered>
            <div className="edit-incident-container">
                <h2 className="heading">Report Incident</h2>

                {incident && (
                    <>
                        <div className="date-time-row">
                            <div className="date-time-field">
                                <h3>Check-in Date</h3>
                                <DatePicker
                                    value={checkInDate}
                                    onChange={(val) => { setCheckInDate(val); setErrors((prev) => clearErrors(prev, 'checkIn')); }}
                                    format="DD/MM/YYYY"
                                    suffixIcon={<CalendarDarkIcon w={18} h={20} />}
                                    className="date-picker" />
                            </div>
                            <div className="date-time-field">
                                <h3>Check-in Time</h3>
                                <TimePicker
                                    value={checkInTimePicker}
                                    onChange={(val) => { setCheckInTimePicker(val); setErrors((prev) => clearErrors(prev, 'checkIn')); }}
                                    onOpenChange={(open) => {
                                        if (!open) return;
                                        if (!checkInTimePicker) setCheckInTimePicker(dayjs());
                                        setErrors((prev) => clearErrors(prev, 'checkIn'));
                                    }}
                                    format="HH:mm"
                                    suffixIcon={<ClockDarkIcon w={18} h={18} />}
                                    className="time-picker" />
                            </div>
                            {errors.checkIn && <span className="field-error date-time-error">{errors.checkIn}</span>}
                        </div>
                        <div className="date-time-row">
                            <div className="date-time-field">
                                <h3>Check-out Date</h3>
                                <DatePicker
                                    value={checkOutDate}
                                    onChange={(val) => { setCheckOutDate(val); setErrors((prev) => clearErrors(prev, 'checkOut')); }}
                                    format="DD/MM/YYYY"
                                    suffixIcon={<CalendarDarkIcon w={18} h={20} />}
                                    className="date-picker" />
                            </div>
                            <div className="date-time-field">
                                <h3>Check-out Time</h3>
                                <TimePicker
                                    value={checkOutTimePicker}
                                    onChange={(val) => { setCheckOutTimePicker(val); setErrors((prev) => clearErrors(prev, 'checkOut')); }}
                                    onOpenChange={(open) => {
                                        if (!open) return;
                                        if (!checkOutTimePicker) setCheckOutTimePicker(dayjs());
                                        setErrors((prev) => clearErrors(prev, 'checkOut'));
                                    }}
                                    format="HH:mm"
                                    suffixIcon={<ClockDarkIcon w={18} h={18} />}
                                    className="time-picker" />
                            </div>
                            {errors.checkOut && <span className="field-error date-time-error">{errors.checkOut}</span>}
                        </div>
                    </>
                )}

                <div className="edit-field dropdown-absolute">
                    <h3>Site</h3>
                    <div className="dropdown-wrapper">
                        <Dropdown
                            options={editOptions.sites}
                            onSelect={(values) => { setSite(values[0]?.value || ''); setErrors((prev) => clearErrors(prev, 'site')); }}
                            values={getSiteDropdownValue()}
                            placeholder="Select Site"
                            searchable
                            disabled={isSiteMode || !!incident} />
                    </div>
                    {renderError('site')}
                </div>

                <div className="edit-field dropdown-absolute">
                    <h3>Problem Category</h3>
                    <div className="dropdown-wrapper">
                        <Dropdown
                            options={availableCategories}
                            onSelect={(values) => { categoryUserChanged.current = true; setProblemCategory(values[0]?.value || ''); setErrors((prev) => clearErrors(prev, 'problemCategory')); }}
                            // Resolved from the site-visible set so the chosen
                            // category still shows in the closed control even when
                            // the "critical only" filter hides it from the list.
                            values={getDropdownValue(problemCategory, siteVisibleCategories)}
                            placeholder="Select Problem Category"
                            searchable
                            listHeader={categoryFilterHeader} />
                    </div>
                    {renderError('problemCategory')}
                </div>

                {(incident || problemCategory) && (
                    <>
                        {showRemedyReference && (
                            <div className="edit-field">
                                <h3>Remedy Reference</h3>
                                <input
                                    type="text"
                                    className={`edit-input${errors.remedyReference ? ' input-error' : ''}`}
                                    value={remedyReference}
                                    maxLength={MAX_REFERENCE_LENGTH}
                                    onChange={(e) => { setRemedyReference(e.target.value); setErrors((prev) => clearErrors(prev, 'remedyReference')); }}
                                    placeholder="Enter Remedy Reference" />
                                {renderError('remedyReference')}
                            </div>
                        )}

                        {effectiveConfig.showKhcReference && (
                            <div className="edit-field">
                                <h3>KHC Reference</h3>
                                <input
                                    type="text"
                                    className={`edit-input${errors.khcReference ? ' input-error' : ''}`}
                                    value={khcReference}
                                    maxLength={MAX_REFERENCE_LENGTH}
                                    onChange={(e) => { setKhcReference(e.target.value); setErrors((prev) => clearErrors(prev, 'khcReference')); }}
                                    placeholder="Enter KHC Reference" />
                                {renderError('khcReference')}
                            </div>
                        )}

                        {effectiveConfig.showMaximoReference && (
                            <div className="edit-field">
                                <h3>Maximo Reference</h3>
                                <input
                                    type="text"
                                    className={`edit-input${errors.maximoReference ? ' input-error' : ''}`}
                                    value={maximoReference}
                                    maxLength={MAX_REFERENCE_LENGTH}
                                    onChange={(e) => { setMaximoReference(e.target.value); setErrors((prev) => clearErrors(prev, 'maximoReference')); }}
                                    placeholder="Enter Maximo Reference" />
                                {renderError('maximoReference')}
                            </div>
                        )}

                        {effectiveConfig.showConceptReference && (
                            <div className="edit-field">
                                <h3>Concept Reference</h3>
                                <input
                                    type="text"
                                    className={`edit-input${errors.conceptReference ? ' input-error' : ''}`}
                                    value={conceptReference}
                                    maxLength={MAX_REFERENCE_LENGTH}
                                    onChange={(e) => { setConceptReference(e.target.value); setErrors((prev) => clearErrors(prev, 'conceptReference')); }}
                                    placeholder="Enter Concept Reference" />
                                {renderError('conceptReference')}
                            </div>
                        )}

                        {effectiveConfig.showPoliceReference && (
                            <>
                                <div className="edit-field">
                                    <h3>Police Assigned</h3>
                                    <div className="dropdown-wrapper">
                                        <Dropdown
                                            options={policeAssignedOptions}
                                            onSelect={(values) => {
                                                const val = values[0]?.value || '';
                                                setPoliceAssigned(val);
                                                setPoliceReference('');
                                                setReason('');
                                                setErrors((prev) => clearErrors(prev, 'policeAssigned', 'policeReference', 'reason'));
                                            }}
                                            values={getDropdownValue(policeAssigned, policeAssignedOptions)}
                                            placeholder="Select" />
                                    </div>
                                    {renderError('policeAssigned')}
                                </div>
                                {policeAssigned === 'yes' && (
                                    <div className="edit-field">
                                        <h3>Police Reference</h3>
                                        <input
                                            type="text"
                                            className={`edit-input${errors.policeReference ? ' input-error' : ''}`}
                                            value={policeReference}
                                            maxLength={MAX_REFERENCE_LENGTH}
                                            onChange={(e) => { setPoliceReference(e.target.value); setErrors((prev) => clearErrors(prev, 'policeReference')); }}
                                            placeholder="Enter Police Reference" />
                                        {renderError('policeReference')}
                                    </div>
                                )}
                                {policeAssigned === 'no' && (
                                    <div className="edit-field">
                                        <h3>Reason (Police not assigned)</h3>
                                        <textarea
                                            className={`edit-textarea${errors.reason ? ' input-error' : ''}`}
                                            value={reason}
                                            maxLength={MAX_REASON_LENGTH}
                                            onChange={(e) => { setReason(e.target.value); setErrors((prev) => clearErrors(prev, 'reason')); }}
                                            placeholder="Enter reason why police were not assigned" />
                                        {renderError('reason')}
                                    </div>
                                )}
                            </>
                        )}

                        {incident && (
                            <div className="edit-field">
                                <h3>Status</h3>
                                <div className="dropdown-wrapper">
                                    <Dropdown
                                        options={editOptions.statuses}
                                        onSelect={(values) => { setStatus(values[0]?.value || ''); setErrors((prev) => clearErrors(prev, 'status')); }}
                                        values={getDropdownValue(status, editOptions.statuses)}
                                        placeholder="Select Status" />
                                </div>
                                {renderError('status')}
                            </div>
                        )}

                        {/* Closing an incident (status → Closed) is completed via
                            the Mark as Complete modal, which captures the Root
                            Cause — there is no inline resolution field here. */}

                        {/* Priority is hidden for retail incidents — they do
                            not require one. */}
                        {!isRetailSite && (
                            <div className="edit-field">
                                <h3>Priority</h3>
                                <div className="dropdown-wrapper">
                                    <Dropdown
                                        options={availablePriorities}
                                        onSelect={(values) => { setPriority(values[0]?.value || ''); setErrors((prev) => clearErrors(prev, 'priority')); }}
                                        values={getDropdownValue(priority, availablePriorities)}
                                        placeholder="Select Priority" />
                                </div>
                                {renderError('priority')}
                            </div>
                        )}

                        <div className="edit-field">
                            <h3>Incident Description</h3>
                            <textarea
                                className={`edit-textarea${errors.description ? ' input-error' : ''}`}
                                value={description}
                                maxLength={MAX_DESCRIPTION_LENGTH}
                                onChange={(e) => { setDescription(e.target.value); setErrors((prev) => clearErrors(prev, 'description')); }}
                                placeholder="Enter incident description" />
                            {renderError('description')}
                        </div>

                        {/* Retail stores have no critical incidents, so the flag
                            isn't offered for them at all. */}
                        {!isRetailSite && (
                            <div className="critical-checkbox">
                                {/* A critical-by-default category forces this on and
                                    locks it — the only way to clear it is to change the
                                    category. On any other category it is free to toggle. */}
                                <Checkbox
                                    checked={isCriticalIncident}
                                    disabled={selectedCategoryIsCritical}
                                    onChange={(e) => { handleCriticalChange(e.target.checked); }}>
                                    Critical Incident
                                </Checkbox>
                            </div>
                        )}

                        {/* Editing an incident only reports it to the
                            distribution list when this is ticked — the same
                            opt-in a diary entry uses. Creating an incident
                            always notifies, so the tick is edit-only. */}
                        {incident && (
                            <div className="distribution-checkbox">
                                <Checkbox
                                    checked={sendToDistribution}
                                    onChange={(e) => setSendToDistribution(e.target.checked)}>
                                    Send report to distribution centre
                                </Checkbox>
                            </div>
                        )}

                        {showMitieDutyManager && (
                            <div className="edit-field">
                                <h3>Mitie Duty Manager Informed</h3>
                                <div className="dropdown-wrapper">
                                    <Dropdown
                                        options={mitieDutyManagerOptions}
                                        onSelect={(values) => {
                                            setMitieDutyManagerInformed((values[0]?.value || '') as MitieDutyManagerInformed | '');
                                            setErrors((prev) => clearErrors(prev, 'mitieDutyManagerInformed'));
                                        }}
                                        values={getDropdownValue(mitieDutyManagerInformed, mitieDutyManagerOptions)}
                                        placeholder="Select an option" />
                                </div>
                                {renderError('mitieDutyManagerInformed')}
                            </div>
                        )}

                        <button type="button" className="update-button" onClick={handleSave} disabled={isSubmitting}>
                            {getSubmitButtonLabel(isSubmitting, !!incident)}
                        </button>

                        {/* Preview the distribution email before sending it out
                            to the whole list — only meaningful when a report
                            will actually be sent. */}
                        {incident && sendToDistribution && onPreview && (
                            <button
                                type="button"
                                className="preview-button"
                                onClick={handlePreview}
                                disabled={isPreviewing}>
                                Preview email
                            </button>
                        )}
                    </>
                )}
            </div>

            <EmailPreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                isLoading={isPreviewing}
                preview={previewData}
                sendsOn="update" />
        </Modal>
    );
};

export default EditIncidentModal;
